import React, { FC } from 'react'
import style from './SignalStrength.module.scss'
import classnames from 'classnames'
import Icon from '../Icon'
import TooltipWrapper from '../TooltipWrapper'

export type SignalStrengthLevel = 0 | 1 | 2 | 3 | 4 | 5
export interface SignalStrengthProps {
  level: SignalStrengthLevel
  invert?: boolean
  showLabel?: boolean
  showWarningIcon?: boolean
  className?: string
  size?: 's' | 'm'
  showTooltip?: boolean
}

function getTooltipText (level: SignalStrengthProps['level']) {

  switch (level) {

    case 5:
      return 'Great'
    case 4:
      return 'Good'
    case 3:
      return 'Okay'
    case 2:
      return 'Poor'
    case 1:
      return 'Very poor. Functionality may be affected.'
    default:
      return 'No Signal'

  }

}

function getLabelText (level: SignalStrengthProps['level']) {

  switch (level) {

    case 5:
      return 'great'
    case 4:
      return 'good'
    case 3:
      return 'okay'
    case 2:
      return 'poor'
    case 1:
      return 'very poor'
    default:
      return 'no signal'

  }

}

function wrapTooltip (content, level, size, className) {
  return <TooltipWrapper
  tooltipText={`Network: ${getTooltipText(level)}`}
  position='bottom'
  tooltipStyles={classnames(style['SignalStrength'], {
    [style['SignalStrength--small']]: size === 's',
  }, className)}
  dataBdd='signal-strength'
>
  {content}
  </TooltipWrapper>
}

const SignalStrength: FC<SignalStrengthProps> = ({ level, invert = false, showLabel = false, showWarningIcon = true, className, size = 'm', showTooltip = true }) => {

  const barClasses = classnames(style['SignalStrength-Bar'], {
    [style['SignalStrength-Bar--invert']]: invert
  })
  const highlight = classnames({
    [style['SignalStrength--highlighted-red']]: level < 2,
    [style['SignalStrength--highlighted-orange']]: level < 3,
    [style['SignalStrength--highlighted-green']]: level >= 3 && invert
  })

  const content = <>
  {
    showWarningIcon &&
    <Icon name='danger' className={classnames(style['SignalStrength--warning'], {
      [style['SignalStrength--warning-visible']]: level <= 1
    })} dataBdd='signal-strength-warning-icon' />
  }
  <div className={style['SignalStrength-Level']} data-bdd='signal-strength-level' >
    <div
      className={classnames(barClasses, style['SignalStrength-0'])}
      data-bdd='signal-strength-level-0'
    />
    <div
      className={classnames(barClasses, style['SignalStrength-1'], {
        [highlight]: level >= 0,
      })}
      data-bdd='signal-strength-level-1'
    />
    <div
      className={classnames(barClasses, style['SignalStrength-2'], {
        [highlight]: level >= 2,
      })}
      data-bdd='signal-strength-level-2'
    />
    <div
      className={classnames(barClasses, style['SignalStrength-3'], {
        [highlight]: level >= 3,
      })}
      data-bdd='signal-strength-level-3'
    />
    <div
      className={classnames(barClasses, style['SignalStrength-4'], {
        [highlight]: level >= 4,
      })}
      data-bdd='signal-strength-level-4'
    />
    <div
      className={classnames(barClasses, style['SignalStrength-5'], {
        [highlight]: level >= 5,
      })}
      data-bdd='signal-strength-level-5'
    />
  </div>
  {
    showLabel &&
    <p className={style['SignalStrength-Label']} data-bdd='signal-strength-label'>{ getLabelText(level) }</p>
  }
  </>

  return showTooltip ? wrapTooltip(content, level, size, className) : content

}

export default SignalStrength
